// frontend/src/pages/CostManagementPage.tsx
// v30.1 - UI OVERHAUL (DARK PREMIUM) & USER GUIDANCE

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Box, Typography, CircularProgress, Alert, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Stepper, Step, StepLabel, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Grid,
    LinearProgress, Card, CardContent, Tooltip, IconButton, Tabs, Tab,
    Collapse, Chip, alpha
} from '@mui/material';
import { motion } from "framer-motion"; // Animaciones Premium

// Iconos
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HistoryIcon from '@mui/icons-material/History';
import TimelineIcon from '@mui/icons-material/Timeline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { useSnackbar } from 'notistack';
import axiosInstance from '../config/axiosInstance';

import { getTransactionDateRange, getCostCoverage, getCostCompleteness, getSavedMapping } from '../services/dataService';
import { TransactionDateRange, CostCoverageRecord, CostCompletenessRecord } from '../types';
import { SYSTEM_FIELDS } from '../config/systemFields';
import OperationsDetailHeader from '../components/Operations/OperationsDetailHeader';
import { SkeletonTable } from '../modules/pricingJourney/components/Skeletons';
import { pageFrameSx } from '../modules/pricingJourney/components/PageHeader';
import { formatPercent } from '../modules/pricingJourney/formatNumber';

// --- ESTILOS COMUNES "DARK PREMIUM" ---
const GLASS_SX = {
    bgcolor: alpha('#1e293b', 0.6),
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: 3,
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    color: '#f8fafc'
};

const TABLE_HEAD_SX = {
    '& th': {
        bgcolor: alpha('#0f172a', 0.8),
        color: '#94a3b8',
        fontWeight: 700,
        fontSize: '0.8rem',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
    }
};

const INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        color: '#f8fafc',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        '& fieldset': { borderColor: '#334155' },
        '&:hover fieldset': { borderColor: '#94a3b8' },
        '&.Mui-focused fieldset': { borderColor: '#38bdf8' },
    },
    '& .MuiInputLabel-root': { color: '#94a3b8' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#38bdf8' },
    '& .MuiSelect-icon': { color: '#94a3b8' }
};

// --- SUB-COMPONENTE: EMPTY STATE GUIDE (NUEVO) ---
const CostFlowGuidance = ({ onAction }: { onAction: () => void }) => {
    return (
        <Paper sx={{ ...GLASS_SX, p: 6, textAlign: 'center', mt: 4 }}>
            <Box sx={{ mb: 3, display: 'inline-flex', p: 2, borderRadius: '50%', bgcolor: alpha('#38bdf8', 0.1) }}>
                <HistoryIcon sx={{ fontSize: 48, color: '#38bdf8' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#f8fafc', mb: 1 }}>
                El margen de las ventas pasadas usa hoy el coste actual
            </Typography>
            <Typography variant="body1" sx={{ color: '#94a3b8', maxWidth: 600, mx: 'auto', mb: 4, lineHeight: 1.6 }}>
                Para calcular el margen real de una venta pasada, el sistema necesita saber cuánto costaba el producto <strong>en esa fecha específica</strong>.
                <br /><br />
                Actualmente, el sistema está utilizando el <em>coste actual del producto</em> como alternativa. 
                Para un margen exacto, indica qué coste tenía cada producto en cada periodo.
            </Typography>

            <Grid container spacing={2} justifyContent="center" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: alpha('#0f172a', 0.4), border: '1px dashed #334155', height: '100%' }}>
                        <Typography variant="caption" color="#64748b" display="block" mb={1}>Venta del 15/01/2023</Typography>
                        <Typography variant="subtitle2" color="#cbd5e1">El sistema busca su coste</Typography>
                        <Typography variant="caption" color="#94a3b8">en esa fecha</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ArrowForwardIcon sx={{ color: '#334155' }} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: alpha('#38bdf8', 0.1), border: '1px solid #38bdf8', height: '100%' }}>
                        <Typography variant="caption" color="#38bdf8" display="block" mb={1}>Con línea temporal</Typography>
                        <Typography variant="subtitle2" color="#f8fafc">Encuentra el coste vigente</Typography>
                        <Typography variant="caption" color="#94a3b8">y el margen es el real</Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Button 
                variant="contained" 
                size="large"
                startIcon={<AddCircleOutlineIcon />}
                onClick={onAction}
                sx={{ 
                    bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 700, px: 4,
                    '&:hover': { bgcolor: '#0ea5e9' }
                }}
            >
                Definir la línea temporal de costes
            </Button>
        </Paper>
    );
};

// --- SUB-COMPONENTE: ASISTENTE DE CARGA GENERAL (RE-ESTILIZADO) ---
type UploadSlot = { label: string; file: File | null; gcsObjectName: string | null; originalFilename: string | null; status: 'pending' | 'uploading' | 'success' | 'error'; error?: string; startDate: Date; endDate: Date; };
type Frequency = 'yearly' | 'half-yearly' | 'quarterly' | 'monthly';

const CostUploadAssistant = ({ onFinish }: { onFinish: () => void }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [activeStep, setActiveStep] = useState(0);
    const [frequency, setFrequency] = useState<Frequency>('yearly');
    const [slots, setSlots] = useState<UploadSlot[]>([]);
    const [universalMapping, setUniversalMapping] = useState<Record<string, string>>({});
    const [firstFileHeaders, setFirstFileHeaders] = useState<string[]>([]);
    const [isMappingConfirmed, setIsMappingConfirmed] = useState(false);
    
    const { data: dateRange, isLoading: isLoadingRange } = useQuery<TransactionDateRange, Error>({ 
        queryKey: ['transactionDateRange'], 
        queryFn: getTransactionDateRange, 
    });

    // Lógica original (Generate Slots) preservada intacta...
    React.useMemo(() => {
        if (!dateRange?.min_date || !dateRange?.max_date) return;
        const generatedSlots: UploadSlot[] = [];
        const startDate = new Date(dateRange.min_date);
        const endDate = new Date(dateRange.max_date);
        let current = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), 1));
        while (current <= endDate) {
            let periodEnd: Date, label: string, start_date: Date;
            switch (frequency) {
                case 'yearly': start_date = new Date(Date.UTC(current.getUTCFullYear(), 0, 1)); periodEnd = new Date(Date.UTC(current.getUTCFullYear(), 11, 31)); label = `Año ${current.getUTCFullYear()}`; break;
                case 'half-yearly': if (current.getUTCMonth() < 6) { start_date = new Date(Date.UTC(current.getUTCFullYear(), 0, 1)); periodEnd = new Date(Date.UTC(current.getUTCFullYear(), 5, 30)); label = `1º Semestre ${current.getUTCFullYear()}`; } else { start_date = new Date(Date.UTC(current.getUTCFullYear(), 6, 1)); periodEnd = new Date(Date.UTC(current.getUTCFullYear(), 11, 31)); label = `2º Semestre ${current.getUTCFullYear()}`; } break;
                case 'quarterly': const quarter = Math.floor(current.getUTCMonth() / 3); start_date = new Date(Date.UTC(current.getUTCFullYear(), quarter * 3, 1)); periodEnd = new Date(Date.UTC(current.getUTCFullYear(), (quarter * 3) + 3, 0)); label = `Q${quarter + 1} ${current.getUTCFullYear()}`; break;
                default: 
                    start_date = new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth(), 1)); 
                    periodEnd = new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + 1, 0)); 
                    // Se usa regex para poner en mayúscula la primera letra
                    label = `${current.toLocaleString('es-ES', { month: 'long', timeZone: 'UTC' }).replace(/^\w/, c => c.toUpperCase())} ${current.getUTCFullYear()}`; 
                    break;
            }
            generatedSlots.push({ label, file: null, gcsObjectName: null, originalFilename: null, status: 'pending', startDate: start_date, endDate: periodEnd });
            current = new Date(Date.UTC(periodEnd.getUTCFullYear(), periodEnd.getUTCMonth() + 1, 1));
        }
        setSlots(generatedSlots);
    }, [dateRange, frequency]);

    const handleFileChangeForSlot = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        if (!event.target.files?.[0]) return;
        const file = event.target.files[0];
        const updateSlotState = (data: Partial<UploadSlot>) => setSlots(p => p.map((s, i) => i === index ? { ...s, ...data } : s));
        updateSlotState({ file, status: 'uploading', error: undefined });
        const formData = new FormData();
        formData.append('file', file);
        try {
            const { data } = await axiosInstance.post<{ headers: string[]; gcs_object_name: string; }>(`/data/upload?file_type=historical_costs`, formData);
            updateSlotState({ gcsObjectName: data.gcs_object_name, originalFilename: file.name });
            if (slots.filter(s => s.status === 'success').length === 0) {
                setFirstFileHeaders(data.headers);
                updateSlotState({ status: 'success' });
                enqueueSnackbar("Archivo cargado. Indica qué columna es cada dato.", { variant: "info" });
            } else {
                if (Object.values(universalMapping).every(col => data.headers.includes(col))) {
                    updateSlotState({ status: 'success' });
                } else {
                    updateSlotState({ status: 'error', error: 'Las columnas no coinciden con las del primer archivo.' });
                }
            }
        } catch (err: any) { updateSlotState({ status: 'error', error: err.response?.data?.detail || 'No se ha podido cargar el archivo.' }); }
    };

    const handleRemoveFileForSlot = (indexToRemove: number) => {
        if (slots[indexToRemove].status === 'success' && slots.filter(s => s.status === 'success').length === 1) {
            setFirstFileHeaders([]); setUniversalMapping({}); setIsMappingConfirmed(false);
            enqueueSnackbar("Columnas reiniciadas.", { variant: "warning" });
        }
        setSlots(p => p.map((s, i) => i === indexToRemove ? { ...s, file: null, gcsObjectName: null, originalFilename: null, status: 'pending', error: undefined } : s));
    };

    const handleConfirmMapping = () => {
        if (SYSTEM_FIELDS.historical_costs.filter(f => f.required).every(f => universalMapping[f.key])) {
            setIsMappingConfirmed(true);
            enqueueSnackbar("Columnas confirmadas.", { variant: "success" });
        } else {
            enqueueSnackbar("Asigna todas las columnas obligatorias (*).", { variant: "warning" });
        }
    };

    const handleMappingChange = (e: SelectChangeEvent<string>, key: string) => setUniversalMapping(p => ({ ...p, [key]: e.target.value }));
    
    const batchProcessMutation = useMutation({
        mutationFn: (p: { files: any[], mapping: any }) => axiosInstance.post('/data/historical-costs/batch-process', p),
        onSuccess: () => { enqueueSnackbar('Línea temporal de costes actualizada.', { variant: 'success' }); onFinish(); },
        onError: (err: any) => enqueueSnackbar(err.response?.data?.detail || "No se ha podido procesar.", { variant: 'error' })
    });

    const handleProcessBatch = () => {
        const files = slots.filter(s => s.status === 'success' && s.gcsObjectName && s.originalFilename).map(s => ({ gcs_object_name: s.gcsObjectName!, original_filename: s.originalFilename!, start_date: s.startDate.toISOString().split('T')[0], end_date: s.endDate.toISOString().split('T')[0] }));
        if (files.length === 0) return enqueueSnackbar("Aún no has cargado ningún archivo de costes.", { variant: "warning" });
        batchProcessMutation.mutate({ files, mapping: universalMapping });
    };

    const handleNext = () => { if (activeStep === 1 && (!slots.some(s => s.status === 'success') || !isMappingConfirmed)) { enqueueSnackbar("Carga al menos un periodo y confirma las columnas.", { variant: "warning" }); return; } setActiveStep(p => p + 1); };
    const hasSuccessfulUploads = slots.some(s => s.status === 'success');

    // Estilos personalizados para el Stepper oscuro
    const stepLabelSx = { '& .MuiStepLabel-label': { color: '#94a3b8' }, '& .MuiStepLabel-label.Mui-active': { color: '#38bdf8', fontWeight: 700 }, '& .MuiStepLabel-label.Mui-completed': { color: '#4ade80' } };

    return (
        <Paper sx={{ ...GLASS_SX, p: 4, mt: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 700 }}>Línea temporal de costes</Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>Organiza tu historial de costes por periodos.</Typography>
            </Box>

            <Stepper activeStep={activeStep} sx={{ mb: 6, '& .MuiStepIcon-root': { color: '#334155' }, '& .MuiStepIcon-root.Mui-active': { color: '#38bdf8' }, '& .MuiStepIcon-root.Mui-completed': { color: '#4ade80' } }}>
                <Step><StepLabel sx={stepLabelSx}>Periodos</StepLabel></Step>
                <Step><StepLabel sx={stepLabelSx}>Archivos y columnas</StepLabel></Step>
                <Step><StepLabel sx={stepLabelSx}>Revisión</StepLabel></Step>
            </Stepper>

            {activeStep === 0 && (
                <Box sx={{ my: 3, maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
                    <Typography gutterBottom sx={{ color: '#f8fafc', mb: 3 }}>¿Cada cuánto cambian tus costes?</Typography>
                    <FormControl fullWidth sx={INPUT_SX}>
                        <InputLabel>Frecuencia de cambio</InputLabel>
                        <Select value={frequency} label="Frecuencia de cambio" onChange={e => setFrequency(e.target.value as Frequency)}>
                            <MenuItem value="yearly">Anual</MenuItem>
                            <MenuItem value="half-yearly">Semestral</MenuItem>
                            <MenuItem value="quarterly">Trimestral</MenuItem>
                            <MenuItem value="monthly">Mensual</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            )}

            {activeStep === 1 && (
                <Box sx={{ my: 3 }}>
                    <Typography gutterBottom sx={{ color: '#cbd5e1', mb: 2 }}>Carga un archivo para cada periodo.</Typography>
                    {isLoadingRange ? <SkeletonTable rows={4} /> : (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            {slots.map((slot, index) => (
                                <Grid item xs={12} md={6} lg={4} key={slot.label}>
                                    <Paper variant="outlined" sx={{ 
                                        p: 2, display: 'flex', alignItems: 'center', gap: 2, height: '100%',
                                        bgcolor: alpha('#0f172a', 0.4), borderColor: slot.status === 'error' ? '#ef4444' : (slot.status === 'success' ? '#4ade80' : '#334155'),
                                        transition: 'all 0.2s', '&:hover': { borderColor: '#38bdf8' }
                                    }}>
                                        {slot.status === 'uploading' ? <CircularProgress size={20} color="info" /> : slot.status === 'success' ? <CheckCircleIcon color="success" /> : slot.status === 'error' ? <ErrorIcon color="error" /> : <CloudUploadIcon sx={{ color: '#64748b' }} />}
                                        <Box flexGrow={1} overflow="hidden">
                                            <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 600 }}>{slot.label}</Typography>
                                            {slot.originalFilename ? <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{slot.originalFilename}</Typography> : <Typography variant="caption" sx={{ color: '#64748b' }}>Pendiente de carga</Typography>}
                                            {slot.error && <Typography variant="caption" color="error">{slot.error}</Typography>}
                                        </Box>
                                        {slot.file ? (
                                            <Button size="small" onClick={() => handleRemoveFileForSlot(index)} sx={{ color: '#ef4444' }}>Quitar</Button>
                                        ) : (
                                            <Button component="label" size="small" disabled={slot.status === 'uploading' || (hasSuccessfulUploads && !isMappingConfirmed)} sx={{ color: '#38bdf8' }}>
                                                Cargar
                                                <input type="file" hidden onChange={e => handleFileChangeForSlot(e, index)} accept=".csv,.xlsx" />
                                            </Button>
                                        )}
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                    
                    {firstFileHeaders.length > 0 && !isMappingConfirmed && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                            <Box mt={4} component={Paper} p={3} sx={{ bgcolor: alpha('#0f172a', 0.6), border: '1px solid #fcd34d' }}>
                                <Typography variant="h6" gutterBottom sx={{ color: '#f8fafc', fontWeight: 700 }}>Columnas del archivo</Typography>
                                <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>Indica qué columna es cada dato. Se aplica a todos los periodos.</Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={TABLE_HEAD_SX}><TableRow><TableCell>Dato</TableCell><TableCell>Columna de tu archivo</TableCell></TableRow></TableHead>
                                        <TableBody>
                                            {SYSTEM_FIELDS.historical_costs.map(f => (
                                                <TableRow key={f.key}>
                                                    <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>{f.label} {f.required && <Box component="span" color="#fcd34d">*</Box>}</TableCell>
                                                    <TableCell sx={{ borderBottom: '1px solid #334155' }}>
                                                        <FormControl fullWidth size="small" sx={INPUT_SX}>
                                                            <Select value={universalMapping[f.key] || ''} onChange={e => handleMappingChange(e, f.key)}>
                                                                {firstFileHeaders.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                                                            </Select>
                                                        </FormControl>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Button variant="contained" sx={{ mt: 3, bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#7dd3fc' } }} onClick={handleConfirmMapping}>
                                    Confirmar columnas
                                </Button>
                            </Box>
                        </motion.div>
                    )}
                </Box>
            )}

            {activeStep === 2 && (
                <Box sx={{ my: 3, textAlign: 'center' }}>
                    <Typography gutterBottom sx={{ color: '#f8fafc', mb: 3 }}>Resumen</Typography>
                    {slots.filter(s => s.status === 'success').map(s => (
                        <Alert key={s.label} icon={<CheckCircleIcon fontSize="inherit" />} severity="success" variant="outlined" sx={{ mb: 1, bgcolor: alpha('#4ade80', 0.1), color: '#4ade80', border: '1px solid #4ade80', textAlign: 'left' }}>
                            <strong>{s.label}</strong>: {s.originalFilename}
                        </Alert>
                    ))}
                    <Typography variant="body2" sx={{ color: '#94a3b8', mt: 3 }}>
                        Al procesar, se recalculan los márgenes históricos con estos costes por periodo.
                    </Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 6, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <Button onClick={onFinish} sx={{ mr: 'auto', color: '#94a3b8' }}>Cancelar</Button>
                {activeStep > 0 && <Button onClick={() => setActiveStep(p => p - 1)} sx={{ mr: 2, color: '#f8fafc', borderColor: '#475569' }} variant="outlined">Atrás</Button>}
                <Button 
                    onClick={activeStep === 2 ? handleProcessBatch : handleNext} 
                    variant="contained" 
                    disabled={isLoadingRange || (activeStep === 1 && (!hasSuccessfulUploads || !isMappingConfirmed)) || (activeStep === 2 && (!hasSuccessfulUploads || batchProcessMutation.isPending))}
                    sx={{ bgcolor: activeStep === 2 ? '#4ade80' : '#38bdf8', color: '#0f172a', fontWeight: 800, '&:hover': { bgcolor: activeStep === 2 ? '#22c55e' : '#0ea5e9' } }}
                >
                    {activeStep === 2 ? (batchProcessMutation.isPending ? 'Procesando…' : 'Procesar') : 'Siguiente'}
                </Button>
            </Box>
        </Paper>
    );
};

// --- SUB-COMPONENTE: VISTA DE DIAGNÓSTICO DE COBERTURA (RE-ESTILIZADO) ---
const CostCoverageView = ({ onEmptyAction }: { onEmptyAction: () => void }) => {
    const [frequency, setFrequency] = useState<'yearly' | 'quarterly' | 'monthly'>('yearly');
    const { data: coverageData, isLoading, isError, error } = useQuery<CostCoverageRecord[], Error>({ queryKey: ['costCoverage', frequency], queryFn: () => getCostCoverage(frequency), refetchOnWindowFocus: false });
    const handleFrequencyChange = (_event: React.SyntheticEvent, newValue: 'yearly' | 'quarterly' | 'monthly') => setFrequency(newValue);
    
    // Función auxiliar de color
    const getColor = (p: number) => p >= 95 ? 'success' : p >= 75 ? 'warning' : 'error';

    if (!isLoading && (!coverageData || coverageData.length === 0)) {
        return <CostFlowGuidance onAction={onEmptyAction} />;
    }

    return (
        <Card sx={GLASS_SX}>
            <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                        <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 700 }}>Cobertura de costes</Typography>
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>Porcentaje de las ventas pasadas que tienen el coste de su fecha.</Typography>
                    </Box>
                    <Tooltip title="El objetivo es el 100 %. Las ventas sin coste de su fecha usan el coste actual, y su margen puede no ser el real.">
                        <IconButton sx={{ color: '#64748b' }}><HelpOutlineIcon /></IconButton>
                    </Tooltip>
                </Box>
                
                <Tabs 
                    value={frequency} 
                    onChange={handleFrequencyChange} 
                    sx={{ 
                        mb: 3, minHeight: 40, borderBottom: '1px solid #334155',
                        '& .MuiTab-root': { color: '#64748b', fontWeight: 600, minHeight: 40 },
                        '& .MuiTab-root.Mui-selected': { color: '#38bdf8' },
                        '& .MuiTabs-indicator': { bgcolor: '#38bdf8' }
                    }}
                >
                    <Tab label="Por año" value="yearly" />
                    <Tab label="Por trimestre" value="quarterly" />
                    <Tab label="Por mes" value="monthly" />
                </Tabs>

                {isLoading ? <SkeletonTable rows={6} /> : isError ? <Alert severity="error" variant="outlined" sx={{ mt: 2, borderColor: '#ef4444', color: '#ef4444' }}>{error.message}</Alert> :
                <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                    <Table>
                        <TableHead sx={TABLE_HEAD_SX}><TableRow><TableCell>Periodo</TableCell><TableCell align="center">Ventas</TableCell><TableCell align="center">Cobertura</TableCell><TableCell sx={{ width: '30%' }}>Estado</TableCell></TableRow></TableHead>
                        <TableBody>
                            {coverageData?.map((row) => (
                                <TableRow key={row.period_label} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                                    <TableCell><Typography variant="subtitle2" sx={{ color: '#e2e8f0' }}>{row.period_label}</Typography></TableCell>
                                    <TableCell align="center" sx={{ color: '#94a3b8' }}>{row.distinct_products_sold}</TableCell>
                                    <TableCell align="center">
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: row.coverage_percentage >= 95 ? '#4ade80' : row.coverage_percentage >= 75 ? '#fcd34d' : '#ef4444' }}>
                                            {formatPercent(row.coverage_percentage)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={row.coverage_percentage} 
                                            color={getColor(row.coverage_percentage)} 
                                            sx={{ height: 6, borderRadius: 3, bgcolor: '#334155' }} 
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>}
            </CardContent>
        </Card>
    );
};

// --- SUB-COMPONENTE: MOTOR DE CORRECCIÓN IN-PLACE (RE-ESTILIZADO) ---
const InPlaceCorrector = ({ periodLabel, startDate, endDate, onCancel, onSuccess }: { periodLabel: string; startDate: string; endDate: string; onCancel: () => void; onSuccess: () => void; }) => {
    // ... (Lógica interna idéntica, solo estilos cambiados) ...
    const { enqueueSnackbar } = useSnackbar();
    const [file, setFile] = useState<File | null>(null);
    const [headers, setHeaders] = useState<string[]>([]);
    const [gcsObject, setGcsObject] = useState<string | null>(null);
    const [mapping, setMapping] = useState<Record<string, string>>({});
    const [step, setStep] = useState<'upload' | 'map' | 'processing' | 'success'>('upload'); 
    const [error, setError] = useState<string | null>(null);

    // Queries y Mutations (Copiadas del original para mantener funcionalidad)
    const processMutation = useMutation({
        mutationFn: (p: any) => axiosInstance.post('/data/historical-costs/batch-process', p),
        onSuccess: () => { enqueueSnackbar(`Lote ${periodLabel} corregido.`, { variant: 'success' }); setStep('success'); },
        onError: (e: any) => { setError(e.response?.data?.detail || "Error."); setStep('map'); }
    });
    const uploadMutation = useMutation({
        mutationFn: (fd: FormData) => axiosInstance.post<{ headers: string[]; gcs_object_name: string; }>(`/data/upload?file_type=historical_costs`, fd),
        onSuccess: async (res) => {
            setHeaders(res.data.headers); setGcsObject(res.data.gcs_object_name); setError(null);
            try {
                const savedMapping = await getSavedMapping('historical_costs');
                if (Object.values(savedMapping).length > 0 && Object.values(savedMapping).every(col => res.data.headers.includes(col))) {
                    enqueueSnackbar("Columnas reconocidas automáticamente.", { variant: 'success' }); setMapping(savedMapping);
                    if(file) processMutation.mutate({ files: [{ gcs_object_name: res.data.gcs_object_name, original_filename: file.name, start_date: startDate, end_date: endDate }], mapping: savedMapping });
                } else setStep('map');
            } catch (err) { setStep('map'); }
        },
        onError: (e: any) => { setError(e.response?.data?.detail || 'No se ha podido subir el archivo.'); setStep('upload'); }
    });
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => { if (e.target.files?.[0]) { const f = e.target.files[0]; setFile(f); setError(null); setStep('processing'); const fd = new FormData(); fd.append('file', f); uploadMutation.mutate(fd); }};
    const handleProcess = () => { if (!gcsObject || !file) return; setStep('processing'); processMutation.mutate({ files: [{ gcs_object_name: gcsObject, original_filename: file.name, start_date: startDate, end_date: endDate }], mapping }); };
    const handleMappingChange = (e: SelectChangeEvent<string>, key: string) => setMapping(p => ({ ...p, [key]: e.target.value }));

    // --- RENDER DEL CORRECTOR CON ESTILO DARK ---
    return (
        <Box sx={{ m: 1, p: 3, bgcolor: alpha('#000', 0.2), borderRadius: 2, border: '1px dashed #334155' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ color: '#f8fafc', fontWeight: 600 }}>Corregir el periodo <span style={{ color: '#38bdf8' }}>{periodLabel}</span></Typography>
            
            {step === 'upload' && (
                <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} sx={{ color: '#94a3b8', borderColor: '#334155', '&:hover': { color: '#f8fafc', borderColor: '#f8fafc' } }} disabled={uploadMutation.isPending}>
                    Elegir otro archivo
                    <input type="file" hidden onChange={handleFileChange} accept=".csv,.xlsx" />
                </Button>
            )}
            
            {step === 'map' && (
                <Box>
                    <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none', mb: 2 }}>
                        <Table size="small">
                            <TableHead sx={TABLE_HEAD_SX}><TableRow><TableCell>Dato</TableCell><TableCell>Columna de tu archivo</TableCell></TableRow></TableHead>
                            <TableBody>
                                {SYSTEM_FIELDS.historical_costs.map(f => (
                                    <TableRow key={f.key}>
                                        <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #334155' }}>{f.label}</TableCell>
                                        <TableCell sx={{ borderBottom: '1px solid #334155' }}>
                                            <FormControl fullWidth size="small" sx={INPUT_SX}>
                                                <Select value={mapping[f.key] || ''} onChange={e => handleMappingChange(e, f.key)}>
                                                    {headers.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Button variant="contained" onClick={handleProcess} sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 700 }}>Aplicar corrección</Button>
                </Box>
            )}
            
            {(uploadMutation.isPending || processMutation.isPending || step === 'processing') && <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', color: '#94a3b8' }}><CircularProgress size={20} color="info" /><Typography>Aplicando…</Typography></Box>}
            
            {step === 'success' && <Alert severity="success" variant="outlined" sx={{ bgcolor: alpha('#4ade80', 0.1), color: '#4ade80', borderColor: '#4ade80' }}>Corrección aplicada.</Alert>}
            
            {error && <Alert severity="error" variant="outlined" sx={{ mt: 2, bgcolor: alpha('#ef4444', 0.1), color: '#ef4444', borderColor: '#ef4444' }}>{error}</Alert>}
            
            <Box mt={2}>
                <Button size="small" onClick={step === 'success' ? onSuccess : onCancel} sx={{ color: '#94a3b8' }}>{step === 'success' ? 'Finalizar' : 'Cancelar'}</Button>
            </Box>
        </Box>
    );
};

// --- SUB-COMPONENTE: VISTA DE ANÁLISIS DE COMPLETITUD (RE-ESTILIZADO) ---
const CompletenessAnalysisViewRow = ({ row }: { row: CostCompletenessRecord; }) => {
    const [open, setOpen] = useState(false);
    const [isCorrecting, setIsCorrecting] = useState(false);
    const queryClient = useQueryClient();
    const matchedProducts = row.distinct_products_sold - (row.missing_products?.length || 0);

    const handleSuccess = () => { setIsCorrecting(false); queryClient.invalidateQueries({ queryKey: ['costCompleteness'] }); queryClient.invalidateQueries({ queryKey: ['costCoverage'] }); };

    return (
        <>
            <TableRow sx={{ '& > *': { borderBottom: '1px solid rgba(255,255,255,0.05)' }, '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                <TableCell><IconButton size="small" onClick={() => setOpen(!open)} sx={{ color: '#64748b' }}>{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton></TableCell>
                <TableCell><Typography variant="subtitle2" sx={{ color: '#e2e8f0' }}>{row.period_label}</Typography></TableCell>
                <TableCell align="center" sx={{ color: '#94a3b8' }}>{row.distinct_products_sold}</TableCell>
                <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: row.completeness_percentage >= 99.9 ? '#4ade80' : '#fcd34d' }}>{formatPercent(row.completeness_percentage)}</Typography>
                </TableCell>
                <TableCell>
                    <LinearProgress variant="determinate" value={row.completeness_percentage} sx={{ height: 6, borderRadius: 3, bgcolor: '#334155', '& .MuiLinearProgress-bar': { bgcolor: row.completeness_percentage >= 99.9 ? '#4ade80' : '#fcd34d' } }} />
                </TableCell>
                <TableCell align="center">
                    <Button size="small" onClick={() => { setOpen(true); setIsCorrecting(true); }} sx={{ color: '#38bdf8', fontWeight: 600 }}>Corregir</Button>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0, borderBottom: 'none' }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        {isCorrecting ? (
                            <InPlaceCorrector periodLabel={row.period_label} startDate={row.period_start} endDate={row.period_end} onCancel={() => setIsCorrecting(false)} onSuccess={handleSuccess} />
                        ) : (
                            <Box sx={{ m: 2, p: 3, bgcolor: alpha('#1e293b', 0.8), borderRadius: 2, border: '1px solid #334155' }}>
                                <Typography variant="h6" gutterBottom sx={{ color: '#f8fafc', fontSize: '0.95rem' }}>Detalle del periodo</Typography>
                                {row.missing_products && row.missing_products.length > 0 ? (
                                    <>
                                        <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 2 }}>
                                            Encontrados costes para <span style={{ color: '#4ade80', fontWeight: 'bold' }}>{matchedProducts}</span> de los {row.distinct_products_sold} productos vendidos en este periodo.
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#fcd34d', fontWeight: 700, mb: 1, display: 'block' }}>
                                            Productos sin coste histórico ({row.missing_products.length}) - Se usa su coste actual:
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 1, bgcolor: 'rgba(0,0,0,0.3)', borderColor: '#334155' }}>
                                            {row.missing_products.map(pId => <Chip key={pId} label={pId} size="small" sx={{ m: 0.5, bgcolor: 'rgba(255,255,255,0.05)', color: '#94a3b8', border: '1px solid #475569' }} />)}
                                        </Paper>
                                    </>
                                ) : (
                                    <Alert severity="success" variant="outlined" sx={{ bgcolor: alpha('#4ade80', 0.1), color: '#4ade80', borderColor: '#4ade80' }}>Todos los productos tienen coste en este periodo.</Alert>
                                )}
                            </Box>
                        )}
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const CostCompletenessView = ({ onEmptyAction }: { onEmptyAction: () => void }) => {
    const { data, isLoading, isError, error } = useQuery<CostCompletenessRecord[], Error>({ queryKey: ['costCompleteness'], queryFn: getCostCompleteness, refetchOnWindowFocus: false });

    if (!isLoading && (!data || data.length === 0)) {
        return <CostFlowGuidance onAction={onEmptyAction} />;
    }

    return (
        <Card sx={GLASS_SX}>
            <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                        <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 700 }}>Línea temporal de costes</Typography>
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>Qué parte de los productos tiene coste en cada periodo cargado.</Typography>
                    </Box>
                    <IconButton sx={{ color: '#64748b' }}><AutoGraphIcon /></IconButton>
                </Box>
                {isLoading ? <SkeletonTable rows={6} /> : isError ? <Alert severity="error">{error.message}</Alert> :
                <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                    <Table>
                        <TableHead sx={TABLE_HEAD_SX}><TableRow><TableCell sx={{ width: '1%' }} /><TableCell>Periodo</TableCell><TableCell align="center">Productos con venta</TableCell><TableCell align="center">Cobertura</TableCell><TableCell>Estado</TableCell><TableCell align="center">Acción</TableCell></TableRow></TableHead>
                        <TableBody>{data?.map((row) => <CompletenessAnalysisViewRow key={row.period_label} row={row} />)}</TableBody>
                    </Table>
                </TableContainer>}
            </CardContent>
        </Card>
    );
};

// --- COMPONENTE PRINCIPAL (MAIN PAGE) ---
const CostManagementPage = () => {
    const [mode, setMode] = useState<'view' | 'upload'>('view');
    const [activeTab, setActiveTab] = useState(0);

    return (
        <Box sx={pageFrameSx}>
            <OperationsDetailHeader
                eyebrow="Operaciones · Costes"
                title="Costes y cobertura"
                description={mode === 'view'
                    ? "Gestiona la línea temporal de costes y revisa su cobertura histórica."
                    : "Define nuevas ventanas de vigencia para los costes históricos."}
                action={
                    <Button
                        variant={mode === 'view' ? "contained" : "outlined"}
                        onClick={() => setMode(p => p === 'view' ? 'upload' : 'view')}
                        startIcon={mode === 'view' ? <AddCircleOutlineIcon /> : <TimelineIcon />}
                        sx={{
                            px: 2.2, py: 1.1, borderRadius: 2, fontWeight: 800,
                            textTransform: 'none',
                            bgcolor: mode === 'view' ? '#38bdf8' : 'transparent',
                            color: mode === 'view' ? '#0f172a' : '#94a3b8',
                            borderColor: '#334155',
                            '&:hover': {
                                bgcolor: mode === 'view' ? '#0ea5e9' : 'rgba(255,255,255,0.05)',
                                borderColor: '#f8fafc',
                                color: mode === 'view' ? '#0f172a' : '#f8fafc'
                            }
                        }}
                    >
                        {mode === 'view' ? 'Definir nueva línea temporal' : 'Volver a cobertura'}
                    </Button>
                }
            />

                {mode === 'view' ? (
                    <Box>
                        <Tabs 
                            value={activeTab} 
                            onChange={(_, v) => setActiveTab(v)} 
                            sx={{ 
                                mb: 4, 
                                '& .MuiTab-root': { color: '#94a3b8', fontSize: '0.95rem', textTransform: 'none', mr: 1 },
                                '& .MuiTab-root.Mui-selected': { color: '#f8fafc', fontWeight: 700 },
                                '& .MuiTabs-indicator': { bgcolor: '#38bdf8', height: 2 }
                            }}
                        >
                            <Tab label="Línea temporal" />
                            <Tab label="Cobertura" />
                        </Tabs>
                        
                        {/* PRC-031/033: contenido de la pestaña con fundido corto. */}
                        <Box role="tabpanel" key={activeTab}>
                            {activeTab === 0 ?
                                <CostCompletenessView onEmptyAction={() => setMode('upload')} /> :
                                <CostCoverageView onEmptyAction={() => setMode('upload')} />
                            }
                        </Box>
                    </Box>
                ) : (
                    <Box>
                        <CostUploadAssistant onFinish={() => setMode('view')} />
                    </Box>
                )}
        </Box>
    );
};

export default CostManagementPage;
